/**
 * Serviço de Cache Inteligente - JurisPrazo V2
 * Cache em memória para otimizar performance
 */

class CacheService {
  constructor() {
    this.cache = new Map();
    this.expiry = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutos padrão
  }

  /**
   * Definir TTL personalizado para diferentes tipos de dados
   */
  getTTL(key) {
    if (key.startsWith('comunicacoes_')) return 10 * 60 * 1000; // 10 min
    if (key.startsWith('prazos_')) return 5 * 60 * 1000; // 5 min
    if (key.startsWith('metricas_')) return 2 * 60 * 1000; // 2 min
    if (key.startsWith('profile_')) return 30 * 60 * 1000; // 30 min
    return this.defaultTTL;
  }

  /**
   * Armazenar dados no cache
   */
  set(key, data) {
    const ttl = this.getTTL(key);
    const expiryTime = Date.now() + ttl;
    
    this.cache.set(key, data);
    this.expiry.set(key, expiryTime);
    
    console.log(`📦 Cache SET: ${key} (TTL: ${Math.round(ttl/1000)}s)`);
    
    // Limpeza automática de itens expirados
    this.cleanup();
  }

  /**
   * Recuperar dados do cache
   */
  get(key) {
    const expiryTime = this.expiry.get(key);
    
    // Verificar se existe e não expirou
    if (!this.cache.has(key) || !expiryTime || Date.now() > expiryTime) {
      this.delete(key);
      console.log(`🗑️ Cache MISS: ${key} (expirado ou não existe)`);
      return null;
    }
    
    const data = this.cache.get(key);
    console.log(`✅ Cache HIT: ${key}`);
    return data;
  }

  /**
   * Verificar se existe no cache e é válido
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Remover item do cache
   */
  delete(key) {
    this.cache.delete(key);
    this.expiry.delete(key);
    console.log(`🗑️ Cache DELETE: ${key}`);
  }

  /**
   * Limpar cache por padrão
   */
  deletePattern(pattern) {
    const keys = Array.from(this.cache.keys());
    const regex = new RegExp(pattern);
    
    keys.forEach(key => {
      if (regex.test(key)) {
        this.delete(key);
      }
    });
    
    console.log(`🧹 Cache PATTERN DELETE: ${pattern} (${keys.length} chaves verificadas)`);
  }

  /**
   * Limpar cache expirado
   */
  cleanup() {
    const now = Date.now();
    const expiredKeys = [];
    
    this.expiry.forEach((expiryTime, key) => {
      if (now > expiryTime) {
        expiredKeys.push(key);
      }
    });
    
    expiredKeys.forEach(key => this.delete(key));
    
    if (expiredKeys.length > 0) {
      console.log(`🧹 Cache CLEANUP: ${expiredKeys.length} itens expirados removidos`);
    }
  }

  /**
   * Limpar todo o cache
   */
  clear() {
    const size = this.cache.size;
    this.cache.clear();
    this.expiry.clear();
    console.log(`🧹 Cache CLEAR: ${size} itens removidos`);
  }

  /**
   * Obter estatísticas do cache
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      memory: this.getMemoryUsage()
    };
  }

  /**
   * Estimar uso de memória (aproximado)
   */
  getMemoryUsage() {
    let size = 0;
    this.cache.forEach((value, key) => {
      size += key.length * 2; // UTF-16
      size += JSON.stringify(value).length * 2;
    });
    return Math.round(size / 1024); // KB
  }

  /**
   * Wrapper para funções assíncronas com cache
   */
  async cached(key, asyncFn, customTTL = null) {
    // Tentar pegar do cache primeiro
    const cached = this.get(key);
    if (cached !== null) {
      return cached;
    }

    // Executar função e cachear resultado
    try {
      const result = await asyncFn();
      if (customTTL) {
        const originalTTL = this.getTTL(key);
        this.defaultTTL = customTTL;
        this.set(key, result);
        this.defaultTTL = originalTTL;
      } else {
        this.set(key, result);
      }
      return result;
    } catch (error) {
      console.error(`❌ Erro na função cached para ${key}:`, error);
      throw error;
    }
  }
}

// Instância singleton
const cacheService = new CacheService();

// Limpeza periódica (a cada 5 minutos)
setInterval(() => {
  cacheService.cleanup();
}, 5 * 60 * 1000);

export default cacheService;
